import React, { useState } from "react";
import AcquisitionRadar from "@/components/AcquisitionRadar";
import { radarText } from "@/i18n/acquisitionTranslations";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { MagnifyingGlass, Sparkle, Buildings, ArrowRight, BookmarkSimple, Trash, Target, ListChecks, X } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { eur, price } from "@/lib/format";
import OpportunityMap from "@/components/OpportunityMap";
import { MatchRow } from "@/components/MatchCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import WebSearchResults from "@/components/WebSearchResults";
import { useAuth } from "@/context/AuthContext";
import { Buildings as PortfolioIcon, Globe } from "@phosphor-icons/react";

const EXAMPLES = [
  "Find buildings around Braga suitable for 80+ student rooms within 3 km of Universidade do Minho.",
  "Find hotels in Northern Portugal suitable for conversion.",
  "Find residential development opportunities in Dubai between €3M and €15M.",
];
const WEB_EXAMPLES = [
  "Find private owners selling buildings in Lisbon under €5 million suitable for student housing.",
  "Private owners selling hotels in Porto for conversion.",
  "Particular vende terreno no Algarve para residência sénior.",
];

function SubBar({ label, value }) {
  const color = value >= 75 ? "#059669" : value >= 50 ? "#C5A059" : "#94a3b8";
  return (
    <div>
      <div className="flex justify-between text-[10px] text-slate-500 mb-0.5"><span>{label}</span><span>{value}</span></div>
      <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden"><div className="h-full rounded-sm" style={{ width: `${value}%`, background: color }} /></div>
    </div>
  );
}

function WhyOpportunity({ score, t }) {
  const [open, setOpen] = useState(false);
  const line = (arr, sym, cls) => arr.map((f, i) => <li key={i} className={`flex gap-1.5 ${cls}`}><span>{sym}</span>{t(`opportunity.factor.${f.code}`, { ...(f.params || {}), defaultValue: f.code })}</li>);
  return (
    <div className="mt-2">
      <button onClick={() => setOpen(!open)} data-testid="why-opportunity-toggle" className="flex items-center gap-1 text-xs font-medium text-[#0B2545] hover:text-[#C5A059]">
        <Sparkle size={13} weight="fill" className="text-[#C5A059]" /> {t("opportunity.whyOpportunity", "Why this opportunity?")}
      </button>
      {open && (
        <div className="mt-2 border-t border-slate-100 pt-2 space-y-2 text-xs" data-testid="why-opportunity-panel">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            <SubBar label={t("opportunity.sub.development_fit", "Development fit")} value={score.sub_scores.development_fit} />
            <SubBar label={t("opportunity.sub.location_fit", "Location fit")} value={score.sub_scores.location_fit} />
            <SubBar label={t("opportunity.sub.size_capacity_fit", "Size / capacity")} value={score.sub_scores.size_capacity_fit} />
            <SubBar label={t("opportunity.sub.price_fit", "Price fit")} value={score.sub_scores.price_fit} />
            <SubBar label={t("opportunity.sub.accessibility", "Accessibility")} value={score.sub_scores.accessibility} />
            <SubBar label={t("opportunity.sub.demand_fit", "Demand fit")} value={score.sub_scores.demand_fit} />
          </div>
          {score.positive.length > 0 && <ul className="space-y-1 text-emerald-600">{line(score.positive, "+", "text-emerald-600")}</ul>}
          {score.weak.length > 0 && <ul className="space-y-1 text-amber-600">{line(score.weak, "~", "text-amber-600")}</ul>}
          {score.missing.length > 0 && <div><div className="text-[10px] uppercase text-slate-400">{t("opportunity.missing", "Missing info")}</div><ul className="space-y-1 text-slate-500">{line(score.missing, "?", "text-slate-500")}</ul></div>}
          {score.disqualifying.length > 0 && <ul className="space-y-1 text-red-500">{line(score.disqualifying, "✕", "text-red-500")}</ul>}
          <div><div className="text-[10px] uppercase text-slate-400">{t("opportunity.investigate", "Investigate next")}</div><ul className="space-y-1 text-slate-600">{line(score.investigate, "→", "text-slate-600")}</ul></div>
          <div className="text-[10px] text-slate-400 italic">{t("opportunity.rulesBased", "Rule-based screening — not an AI prediction.")}</div>
        </div>
      )}
    </div>
  );
}

export default function OpportunityFinder() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(null);
  const [parser, setParser] = useState(null);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(null);
  const [matchProp, setMatchProp] = useState(null);
  const [mode, setMode] = useState("crm");
  const [webResult, setWebResult] = useState(null);
  const [includeUnknown, setIncludeUnknown] = useState(true);
  const [includeAgency, setIncludeAgency] = useState(false);

  const parse = useMutation({
    mutationFn: async (q) => (await api.post("/opportunity/parse", { query: q, lang: i18n.language })).data,
    onSuccess: (d) => { setFilters(d.filters); setParser(d.parser); },
    onError: () => toast.error(t("couldNotParseQuery", "Could not understand the request")),
  });
  const search = useMutation({
    mutationFn: async (f) => (await api.post("/opportunity/search", { filters: f })).data,
    onSuccess: (d) => { setResult(d); if (!d.count) toast.info(t("opportunity.noCandidates", "No CRM candidates matched.")); },
  });
  const webSearch = useMutation({
    mutationFn: async ({ f, unknown, agency }) => (await api.post("/websearch/search", {
      filters: f,
      lang: i18n.language,
      seller_types: [
        "private_owner",
        ...(unknown ? ["unknown"] : []),
        ...(agency ? ["agency"] : []),
      ],
    })).data,
    onSuccess: (d) => { setWebResult(d); if (!d.configured) toast.warning(t("websearch.noProvider", "No external web-search provider is configured.")); },
    onError: (e) => toast.error(e.response?.data?.detail || t("actionFailed", "Action failed")),
  });
  const { data: saved = [], refetch: refetchSaved } = useQuery({ queryKey: ["opp-saved"], queryFn: async () => (await api.get("/opportunity/saved")).data });

  const runSearch = (f) => (mode === "web"
    ? webSearch.mutate({ f, unknown: includeUnknown, agency: includeAgency })
    : search.mutate(f));
  const runFull = async (q) => { const d = await parse.mutateAsync(q); setResult(null); setWebResult(null); runSearch(d.filters); };
  const setF = (k, v) => setFilters({ ...filters, [k]: v });
  const busy = parse.isPending || search.isPending || webSearch.isPending;

  const act = useMutation({
    mutationFn: async ({ url, payload, method = "post" }) => (await api[method](url, payload)).data,
  });
  const saveOpp = (c, status) => act.mutate({ url: "/opportunity/save", payload: { property_id: c.property.id, property_title: c.property.title, opportunity_score: c.score.opportunity_score, status, filters, query } }, { onSuccess: () => { toast.success(status === "watchlist" ? t("opportunity.addedWatchlist", "Added to watchlist") : t("opportunity.saved", "Opportunity saved")); refetchSaved(); } });
  const createRequirement = (c) => act.mutate({ url: "/requirements", payload: { title: `${t("opportunity.reqFrom", "Opportunity")}: ${c.property.title}`, party_type: "developer", development_interest: filters?.dev_uses?.filter((u) => ["student_housing", "senior_living", "mixed_use"].includes(u)) || [], cities: filters?.city ? [filters.city] : [], countries: filters?.country ? [filters.country] : [], investment_size_max: filters?.price_max, active: true } }, { onSuccess: () => toast.success(t("opportunity.reqCreated", "Requirement created")) });
  const createLead = (c) => act.mutate({ url: "/tasks", payload: { title: `${t("opportunity.leadFor", "Property lead")}: ${c.property.title}`, task_type: "Update property", status: "Pending", notes: query } }, { onSuccess: () => toast.success(t("opportunity.leadCreated", "Lead task created")) });
  const followUp = (c) => act.mutate({ url: "/tasks", payload: { title: `${t("opportunity.followUp", "Follow up")}: ${c.property.title}`, task_type: "Follow-up call", status: "Pending", notes: query } }, { onSuccess: () => toast.success(t("opportunity.taskCreated", "Follow-up task created")) });

  return (
    <div>
      <PageHeader title={t("navigation.opportunityFinder", "Opportunity Finder")} subtitle={mode === "web" ? t("websearch.subtitle", "Search permitted public internet sources for NEW private-owner opportunities") : t("opportunity.subtitle", "Describe a development brief in plain language — we screen your portfolio for site opportunities")} />

      <div className="flex gap-1 mb-4 bg-slate-100 rounded-sm p-1 w-fit" data-testid="opp-mode-toggle">
        {[["radar", Target, radarText(i18n.language, "title")], ["crm", PortfolioIcon, t("websearch.modeCrm", "My CRM / Portfolio")], ["web", Globe, t("websearch.modeWeb", "Web Search")]].filter(([m]) => m !== "web" || user?.role === "admin").map(([m, Icon, label]) => (
          <button key={m} data-testid={`opp-mode-${m}`} onClick={() => { setMode(m); setResult(null); setWebResult(null); }}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-sm transition-colors ${mode === m ? "bg-[#0B2545] text-white" : "text-slate-600 hover:bg-white"}`}>
            <Icon size={16} />{label}
          </button>
        ))}
      </div>

      {mode === "radar" && <AcquisitionRadar />}
      {mode !== "radar" && <>
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="opp-query-input" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && query && runFull(query)}
              placeholder={mode === "web" ? t("websearch.placeholder", "e.g. Find private owners selling buildings in Lisbon under €5 million suitable for student housing") : t("opportunity.placeholder", "e.g. Find buildings around Braga for 80+ student rooms under €5M")} className="pl-10 rounded-sm h-11" />
          </div>
          <Button data-testid="opp-search-btn" disabled={!query || busy} onClick={() => runFull(query)} className="rounded-sm h-11 bg-[#0B2545] hover:bg-[#1a365d] text-white px-6">
            {busy ? t("opportunity.searching", "Searching…") : t("opportunity.search", "Search")}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {(mode === "web" ? WEB_EXAMPLES : EXAMPLES).map((ex, i) => (
            <button key={i} data-testid={`opp-example-${i}`} onClick={() => { setQuery(ex); runFull(ex); }} className="text-xs px-3 py-1 rounded-sm border border-slate-200 text-slate-500 hover:border-[#C5A059] hover:text-[#0B2545]">{ex.length > 54 ? ex.slice(0, 54) + "…" : ex}</button>
          ))}
        </div>

        {filters && (
          <div className="mt-4 border-t border-slate-100 pt-4" data-testid="opp-filters">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle size={15} weight="fill" className="text-[#C5A059]" />
              <span className="text-sm font-medium text-slate-700">{t("opportunity.parsedFilters", "Interpreted filters")}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-sm border ${parser === "llm" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{parser === "llm" ? t("opportunity.aiParsed", "AI-interpreted") : t("opportunity.keywordParsed", "keyword")}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {[["city", t("matching.form.cities", "City")], ["region", t("matching.form.regions", "Region")], ["country", t("matching.form.countries", "Country")], ["radius_km", t("opportunity.radius", "Radius (km)")], ["min_units", t("opportunity.minUnits", "Min rooms/units")], ["area_min", t("matching.form.areaMin", "Area min")], ["price_max", t("matching.form.budgetMax", "Max price")], ["near_universities_km", t("opportunity.nearUni", "Near university (km)")]].map(([k, lbl]) => (
                <div key={k}><label className="text-[10px] uppercase tracking-wide text-slate-400">{lbl}</label><Input data-testid={`opp-filter-${k}`} value={filters[k] ?? ""} onChange={(e) => setF(k, e.target.value === "" ? null : (["city", "region", "country"].includes(k) ? e.target.value : Number(e.target.value)))} className="mt-1 rounded-sm h-9 text-sm" /></div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(filters.dev_uses || []).map((u) => <span key={u} className="text-xs px-2 py-0.5 rounded-sm bg-[#0B2545] text-white">{t(`opportunity.use.${u}`, u)}</span>)}
              {(filters.build_types || []).map((b) => <span key={b} className="text-xs px-2 py-0.5 rounded-sm border border-slate-200 text-slate-600">{t(`opportunity.build.${b}`, b)}</span>)}
            </div>
            <Button data-testid="opp-run-filters" onClick={() => runSearch(filters)} disabled={busy} className="mt-4 rounded-sm bg-[#C5A059] hover:bg-[#b28f4a] text-white">{t("opportunity.runSearch", "Run search")} <ArrowRight size={15} className="ml-1" /></Button>
          </div>
        )}
      </div>

      {mode === "web" && (
        <div className="mt-6" data-testid="web-search-panel">
          <WebSearchResults
            result={webResult}
            includeUnknown={includeUnknown}
            includeAgency={includeAgency}
            showAgencyToggle={user?.role === "admin"}
            onToggleUnknown={(v) => {
              setIncludeUnknown(v);
              if (filters) webSearch.mutate({ f: filters, unknown: v, agency: includeAgency });
            }}
            onToggleAgency={(v) => {
              setIncludeAgency(v);
              if (filters) webSearch.mutate({ f: filters, unknown: includeUnknown, agency: v });
            }}
          />
        </div>
      )}

      {mode === "crm" && result && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-serif text-lg text-slate-800">{t("opportunity.candidates", "Candidates")} <span className="text-slate-400 text-sm">({result.count})</span></h3>
            </div>
            <OpportunityMap candidates={result.candidates} target={result.target} radiusKm={result.filters?.radius_km} onSelect={setSelected} />
            {result.target && <p className="text-xs text-slate-400 mt-1">{t("opportunity.centeredOn", "Centered on")}: {result.target.label}</p>}
          </div>
          <div className="space-y-3 max-h-[640px] overflow-y-auto pr-1" data-testid="opp-candidates">
            {result.candidates.length === 0 && <p className="text-slate-400 text-sm">{t("opportunity.noCandidates", "No CRM candidates matched.")}</p>}
            {result.candidates.map((c) => (
              <div key={c.property.id} data-testid={`opp-candidate-${c.property.id}`} className={`bg-white border rounded-sm p-4 ${selected?.property.id === c.property.id ? "border-[#C5A059]" : "border-slate-200"}`}>
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-sm flex items-center justify-center text-white font-serif flex-shrink-0" style={{ background: c.score.opportunity_score >= 75 ? "#059669" : c.score.opportunity_score >= 55 ? "#C5A059" : "#94a3b8" }}>{c.score.opportunity_score}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{c.property.title}</div>
                    <div className="text-xs text-slate-500">{[c.property.city, c.property.category].filter(Boolean).join(" · ")}{c.score.distance_km != null ? ` · ${c.score.distance_km} km` : ""}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{price(c.property.asking_price)}{c.score.estimated_capacity ? ` · ~${c.score.estimated_capacity} ${t("opportunity.units", "units")}` : ""} · {t("scoring.confidence", "Confidence")} {c.score.confidence}%</div>
                  </div>
                </div>
                <WhyOpportunity score={c.score} t={t} />
                <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100">
                  <Button size="sm" variant="outline" className="rounded-sm text-xs h-7" data-testid={`opp-match-${c.property.id}`} onClick={() => setMatchProp(c.property)}><Target size={13} className="mr-1" />{t("opportunity.matchDevelopers", "Match")}</Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-xs h-7" onClick={() => saveOpp(c, "saved")}><BookmarkSimple size={13} className="mr-1" />{t("opportunity.save", "Save")}</Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-xs h-7" data-testid={`opp-watch-${c.property.id}`} onClick={() => saveOpp(c, "watchlist")}>{t("opportunity.watchlist", "Watchlist")}</Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-xs h-7" onClick={() => createRequirement(c)}>{t("opportunity.createRequirement", "Requirement")}</Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-xs h-7" onClick={() => createLead(c)}>{t("opportunity.createLead", "Lead")}</Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-xs h-7" onClick={() => followUp(c)}>{t("opportunity.followUpTask", "Follow-up")}</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === "crm" && saved.length > 0 && (
        <div className="mt-8">
          <h3 className="font-serif text-lg text-slate-800 mb-3 flex items-center gap-2"><ListChecks size={18} className="text-[#C5A059]" /> {t("opportunity.savedWatchlist", "Saved & Watchlist")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="opp-saved">
            {saved.map((s) => (
              <div key={s.id} className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-[#0B2545] text-white flex items-center justify-center font-serif">{s.opportunity_score ?? "—"}</div>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{s.property_title}</div><div className="text-xs text-slate-400">{t(`opportunity.status.${s.status}`, s.status)}</div></div>
                <button onClick={() => act.mutate({ url: `/opportunity/saved/${s.id}`, method: "delete" }, { onSuccess: () => refetchSaved() })} className="text-slate-400 hover:text-red-600"><Trash size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Sheet open={!!matchProp} onOpenChange={(o) => !o && setMatchProp(null)}>
        <SheetContent className="bg-white w-full sm:max-w-lg overflow-y-auto" data-testid="opp-match-sheet">
          {matchProp && <MatchSheet property={matchProp} t={t} />}
        </SheetContent>
      </Sheet>
      </>}
    </div>
  );
}

function MatchSheet({ property, t }) {
  const { data } = useQuery({ queryKey: ["opp-matches", property.id], queryFn: async () => (await api.get(`/opportunity/${property.id}/matches`)).data });
  const matches = (data?.matches || []).filter((m) => m.match.score >= 40);
  return (
    <>
      <SheetHeader><SheetTitle className="font-serif text-xl">{t("opportunity.matchingDevInv", "Matching developers & investors")}</SheetTitle></SheetHeader>
      {data?.recommended && (
        <div className="mt-4 bg-[#0B2545]/5 border border-[#C5A059]/30 rounded-sm p-3" data-testid="opp-recommended">
          <div className="text-[10px] uppercase tracking-wide text-[#C5A059]">{t("opportunity.recommendedFirst", "Contact first")}</div>
          <div className="text-sm font-medium text-[#0B2545]">{data.recommended.party.name} · {data.recommended.match.score}%</div>
        </div>
      )}
      <div className="mt-4 space-y-2">
        {matches.length === 0 ? <p className="text-slate-400 text-sm">{t("matching.noMatches", "No matches yet.")}</p> :
          matches.map((m) => <MatchRow key={m.party.id} name={m.party.name} subtitle={t(`matching.party.${m.party.party_type}`, m.party.party_type)} match={m.match} testid={`opp-dev-${m.party.id}`} />)}
      </div>
    </>
  );
}
